import React, { useEffect, useMemo, useRef, useState } from "react";
import QEEGTrendLane from "./QEEGTrendLane";
import ArtifactOverlayControls from "./ArtifactOverlayControls";
import ArtifactReviewDashboard, {
  type ArtifactWorkbenchRow,
} from "./ArtifactReviewDashboard";
import ClinicalTimelinePanel from "./ClinicalTimelinePanel";
import ReviewAccelerationToolkit from "./ReviewAccelerationToolkit";
import ClinicalReviewCollaborationPanel, {
  type ConsensusStatus,
} from "./ClinicalReviewCollaborationPanel";
import { useReviewAccelerationState } from "../hooks/useReviewAccelerationState";
import { fetchClinicalTimeline, persistArtifactQcDecision, persistArtifactReductionAudit } from "../services/eegEventApi";
import ArtifactDetectionService from "../services/ArtifactDetection";
import apiClient from "@shared/services/apiClient";
import { RegulatoryPhaseGate } from "@shared/hooks/useFeatureFlags";
import {
  fetchQeegLongStudyTrends,
  type QeegLongStudyClientSeries,
} from "../utils/qeegLongStudyTrendClient";
import timescaleAPI from "@shared/services/timescaleApiClient";
import type { QEEGTrendSample } from "../utils/qeegTrendTransforms";
import type {
  ClinicalEvent,
  ClinicalEventKind,
  ClinicalTimelineDensityMode,
} from "../types/clinicalEvent";
import {
  ARTIFACT_OVERLAY_TYPES,
  filterArtifactsByControls,
  mapDetectorTypeToOverlay,
  type ClassifiedArtifact,
} from "../utils/artifactClassification";
import {
  liveDetectorArtifactToOverlay,
  mergeArtifactOverlays,
} from "../utils/liveArtifactOverlay";
import {
  createDefaultReductionState,
  type ArtifactReductionState,
} from "../utils/artifactQcTypes";
import {
  ARTIFACT_REDUCTION_MODEL_CARD_URI,
  hashTraceSamples,
  reduceChannelSamples,
} from "../utils/artifactReductionEngine";
import { setArtifactReductionDisplaySnapshot } from "../utils/artifactReductionDisplayBridge";
import type { DockPanelKey } from "./dockPanelLabels";

export type MarketabilityDockPanelsProps = {
  panelKey: DockPanelKey;
  recordingId?: string | null;
  studyId?: string | null;
  currentTime: number;
  recordingDuration: number;
  sampleRate: number;
  channelData?: Float32Array | number[];
  leftChannelData?: Float32Array | number[];
  rightChannelData?: Float32Array | number[];
  channelNames?: string[];
  badChannels?: string[];
  montageName?: string | null;
  highPassHz?: number | null;
  lowPassHz?: number | null;
  notch50Hz?: boolean;
  notch60Hz?: boolean;
  onSeek: (timeSeconds: number) => void;
};

function toBookmarkEvent(
  recordingId: string,
  studyId: string,
  currentTime: number,
): ClinicalEvent {
  return {
    eventId: `bookmark-${Date.now()}`,
    studyId: studyId || "00000000-0000-4000-8000-000000000000",
    recordingId: recordingId || "00000000-0000-4000-8000-000000000000",
    kind: "bookmark",
    sourceKind: "manual",
    eventTypeCode: "bookmark",
    eventLabel: `Bookmark @ ${currentTime.toFixed(1)}s`,
    startTimeSeconds: currentTime,
    endTimeSeconds: null,
    channels: [],
    eventStatus: "active",
    payload: { kind: "bookmark" },
  };
}

function toNumberArray(data?: Float32Array | number[]): number[] {
  if (!data) return [];
  return Array.isArray(data) ? data.map((value) => Number(value) || 0) : Array.from(data);
}

function publishViewerInteractionCost(partial: {
  overlayCount?: number;
  qeegLaneRenderCostMs?: number;
}): void {
  if (typeof window === "undefined") return;
  const existing = ((window as any).__eegPlaybackPerf || {}) as Record<
    string,
    unknown
  >;
  const latest = (existing.latest || {}) as Record<string, unknown>;
  (window as any).__eegPlaybackPerf = {
    ...existing,
    updatedAt: new Date().toISOString(),
    latest: {
      ...latest,
      ...partial,
    },
  };
}

export default function MarketabilityDockPanels({
  panelKey,
  recordingId,
  studyId,
  currentTime,
  recordingDuration,
  sampleRate,
  channelData,
  leftChannelData,
  rightChannelData,
  channelNames = [],
  badChannels = [],
  montageName = null,
  highPassHz = null,
  lowPassHz = null,
  notch50Hz = false,
  notch60Hz = false,
  onSeek,
}: MarketabilityDockPanelsProps) {
  const [qeegVisible, setQeegVisible] = useState(true);
  const [overlayEnabled, setOverlayEnabled] = useState(true);
  const [confidence, setConfidence] = useState(0.5);
  const [enabledArtifactTypes, setEnabledArtifactTypes] = useState([
    ...ARTIFACT_OVERLAY_TYPES,
  ]);
  const [timelineEvents, setTimelineEvents] = useState<ClinicalEvent[]>([]);
  const [liveArtifacts, setLiveArtifacts] = useState<ClassifiedArtifact[]>([]);
  const [artifactDecisions, setArtifactDecisions] = useState<
    Record<string, ArtifactWorkbenchRow["decision"]>
  >({});
  const [densityBuckets, setDensityBuckets] = useState<
    Array<{ start: number; end: number; total: number }>
  >([]);
  const [densityMode, setDensityMode] =
    useState<ClinicalTimelineDensityMode>("normal");
  const [enabledKinds, setEnabledKinds] = useState<ClinicalEventKind[]>([
    "annotation",
    "medication",
    "vital",
    "conversion_milestone",
    "report_action",
    "artifact",
    "qeeg_marker",
    "bookmark",
    "clip",
    "ai_detection",
    "video",
    "upload_milestone",
  ]);
  const [consensusStatus, setConsensusStatus] =
    useState<ConsensusStatus>("in_review");
  const [handoffNote, setHandoffNote] = useState("");
  const [longStudySeries, setLongStudySeries] = useState<
    QeegLongStudyClientSeries[] | null
  >(null);
  const [longStudyStatus, setLongStudyStatus] = useState<
    | "idle"
    | "loading"
    | "ready"
    | "partial"
    | "unavailable"
    | "generating"
    | "failed"
  >("idle");
  const [longStudyProvenance, setLongStudyProvenance] = useState<
    import("../utils/qeegTrendTransforms").QEEGTrendProvenance | null
  >(null);
  const [seizureBurdenSamples, setSeizureBurdenSamples] = useState<
    QEEGTrendSample[] | null
  >(null);
  const [reductionState, setReductionState] = useState<ArtifactReductionState>(
    createDefaultReductionState,
  );
  const review = useReviewAccelerationState();
  const channelNamesKey = channelNames.join("\0");
  const signalSampleCount =
    (channelData?.length || 0) +
    (leftChannelData?.length || 0) +
    (rightChannelData?.length || 0);
  const currentTimeRef = useRef(currentTime);
  currentTimeRef.current = currentTime;
  const channelDataRef = useRef(channelData);
  channelDataRef.current = channelData;
  const leftChannelDataRef = useRef(leftChannelData);
  leftChannelDataRef.current = leftChannelData;
  const rightChannelDataRef = useRef(rightChannelData);
  rightChannelDataRef.current = rightChannelData;
  // Keep timeline fetches on a stable recording span. Using currentTime here
  // re-requested the panel on every playback tick and made pop-outs flash.
  const timelineFetchEndSeconds =
    recordingDuration && recordingDuration > 0
      ? recordingDuration
      : 1;

  useEffect(() => {
    if (!recordingId) return;
    if (
      panelKey !== "clinical_timeline" &&
      panelKey !== "review_accel" &&
      panelKey !== "artifact_quality"
    ) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const payload = await fetchClinicalTimeline({
          recordingId,
          studyId: studyId || undefined,
          start: 0,
          end: Math.max(1, timelineFetchEndSeconds),
          bucketSec: 5,
          kinds: enabledKinds,
          densityMode,
          includeAdapters: true,
        });
        if (cancelled) return;
        setTimelineEvents(payload.events);
        setDensityBuckets(
          payload.buckets.map((bucket) => ({
            start: bucket.start,
            end: bucket.end,
            total: bucket.total,
          })),
        );
      } catch {
        if (!cancelled) {
          setTimelineEvents([]);
          setDensityBuckets([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    panelKey,
    recordingId,
    studyId,
    timelineFetchEndSeconds,
    enabledKinds,
    densityMode,
  ]);

  useEffect(() => {
    if (panelKey !== "qeeg_trends" || !recordingId) {
      return;
    }
    let cancelled = false;
    setLongStudyStatus("loading");
    void (async () => {
      const result = await fetchQeegLongStudyTrends({
        recordingId,
        fromSeconds: 0,
        toSeconds: Math.max(1, recordingDuration || 1),
      });
      if (cancelled) return;
      if (result.ok && result.data) {
        setLongStudySeries(result.data.series);
        const p = result.data.provenance;
        setLongStudyProvenance(
          p
            ? {
                algorithmVersion: p.algorithmVersion,
                channelSet: p.channelSet || [],
                montage: p.montage ?? null,
                samplingRateHz: p.samplingRateHz,
                filters: {
                  highPassHz: p.filters?.highPassHz ?? null,
                  lowPassHz: p.filters?.lowPassHz ?? null,
                  notch50Hz: Boolean(p.filters?.notch50Hz),
                  notch60Hz: Boolean(p.filters?.notch60Hz),
                },
                artifactExclusions: p.artifactExclusions || [],
                epochSeconds: p.epochSeconds,
                generatedAtIso: p.generatedAtIso,
              }
            : null,
        );
        setLongStudyStatus(
          result.data.progress.status === "partial" ? "partial" : "ready",
        );
        return;
      }
      setLongStudySeries(null);
      setLongStudyProvenance(result.data?.provenance ?? null);
      const status = result.data?.progress?.status;
      if (status === "generating") {
        setLongStudyStatus("generating");
      } else if (status === "failed") {
        setLongStudyStatus("failed");
      } else {
        setLongStudyStatus("unavailable");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [panelKey, recordingId, recordingDuration]);

  useEffect(() => {
    if (panelKey !== "qeeg_trends" || !studyId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        // Prefer CX-G04 monitoring history (5-min windows); fall back to daily.
        let samples: QEEGTrendSample[] = [];
        try {
          const historyResp = await apiClient.get(
            `/monitoring/seizure-burden/${studyId}/history`,
            { params: { limit: 288 } },
          );
          const rows =
            historyResp.data?.data ||
            historyResp.data?.items ||
            historyResp.data ||
            [];
          if (Array.isArray(rows) && rows.length > 0) {
            samples = rows.map(
              (
                row: {
                  window_start?: string;
                  windowStart?: string;
                  burden_percent?: number;
                  burdenPercent?: number;
                },
                index: number,
              ) => {
                const start = Date.parse(
                  String(row.window_start || row.windowStart || ""),
                );
                const t =
                  Number.isFinite(start) && recordingDuration > 0
                    ? Math.max(
                        0,
                        Math.min(
                          recordingDuration,
                          (index / Math.max(1, rows.length - 1)) *
                            recordingDuration,
                        ),
                      )
                    : index * 300;
                const pct = Number(row.burden_percent ?? row.burdenPercent ?? 0);
                return {
                  timeSeconds: t,
                  value: Math.max(0, Math.min(1, pct / 100)),
                };
              },
            );
          }
        } catch {
          samples = [];
        }
        if (samples.length === 0) {
          const daily = await timescaleAPI.getDailySeizureBurden(studyId, 14);
          samples = (daily || []).map((row, index) => {
            const start = Date.parse(row.window_start);
            const t =
              Number.isFinite(start) && recordingDuration > 0
                ? Math.max(
                    0,
                    Math.min(
                      recordingDuration,
                      (index / Math.max(1, daily.length - 1)) *
                        recordingDuration,
                    ),
                  )
                : index * 3600;
            return {
              timeSeconds: t,
              value: Math.max(0, Math.min(1, (row.burden_percent || 0) / 100)),
            };
          });
        }
        if (!cancelled) setSeizureBurdenSamples(samples);
      } catch {
        if (!cancelled) setSeizureBurdenSamples([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [panelKey, studyId, recordingDuration]);

  useEffect(() => {
    if (panelKey !== "artifact_quality") return;

    let cancelled = false;
    const rate = sampleRate > 0 ? sampleRate : 256;
    const names =
      channelNames.length > 0
        ? channelNames
        : ["Ch1", "Ch2", "Ch3"].slice(
            0,
            [
              channelDataRef.current,
              leftChannelDataRef.current,
              rightChannelDataRef.current,
            ].filter(Boolean).length || 1,
          );

    const signals = [
      toNumberArray(channelDataRef.current),
      toNumberArray(leftChannelDataRef.current),
      toNumberArray(rightChannelDataRef.current),
    ]
      .filter((signal) => signal.length > 0)
      .slice(0, names.length);

    void (async () => {
      const started = performance.now();
      const localLive: ClassifiedArtifact[] = [];

      if (signals.length > 0) {
        try {
          const detector = new ArtifactDetectionService();
          const result = detector.detectMultiChannelArtifacts(
            signals,
            names.slice(0, signals.length),
            rate,
          );
          result.detections.forEach((detection, index) => {
            const mapped = liveDetectorArtifactToOverlay(detection, index);
            if (mapped) localLive.push(mapped);
          });
        } catch {
          // Local detector is best-effort.
        }
      }

      if (recordingId && signals.length > 0) {
        try {
          const response = await apiClient.post(
            `/analysis/artifacts/${recordingId}/detect`,
            {
              samplingRate: rate,
              windowStartSeconds: Math.max(0, currentTimeRef.current - 2),
              persist: false,
              channels: signals.map((samples, index) => ({
                name: names[index] || `Ch${index + 1}`,
                index,
                samples: samples.slice(0, Math.min(samples.length, rate * 8)),
              })),
            },
          );
          const remote = (response.data?.data || []) as Array<
            Record<string, unknown>
          >;
          remote.forEach((item, index) => {
            const mapped = liveDetectorArtifactToOverlay(
              {
                type: String(item.type || ""),
                startTime:
                  typeof item.startTime === "number"
                    ? item.startTime
                    : undefined,
                endTime:
                  typeof item.endTime === "number" ? item.endTime : undefined,
                channelName:
                  typeof item.channelName === "string"
                    ? item.channelName
                    : undefined,
                confidence:
                  typeof item.confidence === "number"
                    ? item.confidence
                    : undefined,
                description:
                  typeof item.description === "string"
                    ? item.description
                    : undefined,
              },
              index + 1000,
            );
            if (mapped) localLive.push(mapped);
          });
        } catch {
          // Remote live detect is optional when offline/mocked.
        }
      }

      if (!cancelled) {
        setLiveArtifacts(localLive);
        publishViewerInteractionCost({
          overlayCount: localLive.length,
          qeegLaneRenderCostMs: Number(
            (performance.now() - started).toFixed(1),
          ),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [panelKey, recordingId, sampleRate, channelNamesKey, signalSampleCount]);

  const timelineArtifacts: ClassifiedArtifact[] = useMemo(() => {
    return timelineEvents
      .filter((event) => event.kind === "artifact")
      .map((event) => {
        const mapped =
          mapDetectorTypeToOverlay(event.eventTypeCode) ||
          mapDetectorTypeToOverlay(String(event.payload.artifact_type || "")) ||
          "muscle";
        return {
          id: event.eventId,
          type: mapped,
          startTimeSeconds: event.startTimeSeconds,
          endTimeSeconds: event.endTimeSeconds ?? event.startTimeSeconds,
          channels: event.channels,
          confidence: event.confidenceScore ?? 0.6,
          label: event.eventLabel,
        };
      });
  }, [timelineEvents]);

  const artifacts = useMemo(
    () => mergeArtifactOverlays(timelineArtifacts, liveArtifacts),
    [timelineArtifacts, liveArtifacts],
  );
  const artifactWorkbenchRows: ArtifactWorkbenchRow[] = useMemo(
    () =>
      artifacts.slice(0, 12).map((artifact) => {
        const rate = sampleRate > 0 ? sampleRate : 256;
        const candidates = [
          toNumberArray(channelDataRef.current),
          toNumberArray(leftChannelDataRef.current),
          toNumberArray(rightChannelDataRef.current),
        ];
        const source =
          candidates.find((samples) => samples.length > 0) || [];
        const windowStart = Math.max(0, artifact.startTimeSeconds - 1);
        const windowEnd = Math.max(
          windowStart + 0.5,
          artifact.endTimeSeconds + 1,
        );
        const startIdx = Math.floor(windowStart * rate);
        const endIdx = Math.min(source.length, Math.ceil(windowEnd * rate));
        const segment =
          source.length > 0
            ? source.slice(startIdx, Math.max(startIdx + 1, endIdx))
            : [artifact.startTimeSeconds, artifact.endTimeSeconds];
        const beforeTraceHash = hashTraceSamples(segment);
        const reduced = reduceChannelSamples({
          samples: segment,
          sampleRate: rate,
          windowStartSeconds: windowStart,
          reducedTypes:
            reductionState.reducedTypes.length > 0
              ? reductionState.reducedTypes
              : [artifact.type],
          channelName: artifact.channels[0],
          intervals: [
            {
              type: artifact.type,
              channel: artifact.channels[0],
              startSeconds: artifact.startTimeSeconds,
              endSeconds: artifact.endTimeSeconds,
            },
          ],
        });
        const afterTraceHash =
          artifactDecisions[artifact.id] === "accept" ||
          (reductionState.enabled && !reductionState.showOriginalSignal)
            ? reduced.afterTraceHash
            : beforeTraceHash;
        return {
          artifactId: artifact.id,
          channel: artifact.channels[0] || "unknown",
          detectorId: artifact.type,
          startSeconds: artifact.startTimeSeconds,
          endSeconds: artifact.endTimeSeconds,
          decision: artifactDecisions[artifact.id] || "pending",
          beforeTraceHash,
          afterTraceHash,
        };
      }),
    [artifacts, artifactDecisions, reductionState, sampleRate, signalSampleCount],
  );

  useEffect(() => {
    if (panelKey !== "artifact_quality") {
      setArtifactReductionDisplaySnapshot(null);
      return;
    }
    setArtifactReductionDisplaySnapshot({
      state: reductionState,
      artifacts,
      updatedAtIso: new Date().toISOString(),
    });
    return () => setArtifactReductionDisplaySnapshot(null);
  }, [panelKey, reductionState, artifacts]);

  useEffect(() => {
    if (panelKey !== "artifact_quality" && panelKey !== "qeeg_trends") return;
    const visibleOverlayCount = filterArtifactsByControls(artifacts, {
      enabledTypes: enabledArtifactTypes,
      confidenceThreshold: confidence,
    }).length;
    publishViewerInteractionCost({
      overlayCount: overlayEnabled ? visibleOverlayCount : 0,
    });
  }, [
    panelKey,
    artifacts,
    enabledArtifactTypes,
    confidence,
    overlayEnabled,
  ]);

  useEffect(() => {
    if (panelKey !== "qeeg_trends") return;
    const started = performance.now();
    const frame = requestAnimationFrame(() => {
      publishViewerInteractionCost({
        qeegLaneRenderCostMs: Number((performance.now() - started).toFixed(1)),
      });
    });
    return () => cancelAnimationFrame(frame);
  }, [panelKey, channelData, leftChannelData, rightChannelData, currentTime]);

  const channelQuality = useMemo(
    () =>
      channelNames.map((channel) => ({
        channel,
        isBad: badChannels.includes(channel),
        snrDb: badChannels.includes(channel) ? 6 : 22,
        impedanceKohms: badChannels.includes(channel) ? 18 : 4,
      })),
    [channelNames, badChannels],
  );

  if (panelKey === "qeeg_trends") {
    return (
      <QEEGTrendLane
        channelData={channelData}
        leftChannelData={leftChannelData}
        rightChannelData={rightChannelData}
        sampleRate={sampleRate || 256}
        currentTimeSeconds={currentTime}
        recordingDurationSeconds={recordingDuration || 1}
        visible={qeegVisible}
        onToggleVisible={setQeegVisible}
        onSeek={onSeek}
        channelNames={channelNames}
        montageName={montageName}
        highPassHz={highPassHz}
        lowPassHz={lowPassHz}
        notch50Hz={notch50Hz}
        notch60Hz={notch60Hz}
        artifactExclusions={badChannels}
        longStudySeries={longStudySeries}
        longStudyStatus={longStudyStatus}
        provenanceOverride={longStudyProvenance}
        seizureBurdenSamples={seizureBurdenSamples}
        vsBaselineEnabled={review.state.baselineComparisonEnabled}
        baselineFromSeconds={0}
        baselineToSeconds={Math.min(300, Math.max(60, recordingDuration * 0.05))}
      />
    );
  }

  if (panelKey === "artifact_quality") {
    return (
      <RegulatoryPhaseGate
        phase={2}
        fallback={
          <div className="eeg-dock-panel-status" data-testid="artifact-qc-phase-gate">
            Artifact QC requires Phase 2 ARTIFACT_DETECTION clearance.
          </div>
        }
      >
        <div className="eeg-artifact-dock-panel">
          <ArtifactOverlayControls
            artifacts={artifacts}
            channelQuality={channelQuality}
            overlayEnabled={overlayEnabled}
            onOverlayEnabledChange={setOverlayEnabled}
            confidenceThreshold={confidence}
            onConfidenceThresholdChange={setConfidence}
            enabledTypes={enabledArtifactTypes}
            onEnabledTypesChange={setEnabledArtifactTypes}
            onSeek={onSeek}
            modelCardUri={ARTIFACT_REDUCTION_MODEL_CARD_URI}
            onReductionStateChange={setReductionState}
            onReductionAudit={(action) => {
              if (!recordingId || !studyId) return;
              void persistArtifactReductionAudit({
                studyId,
                recordingId,
                action: action.action,
                reducedTypes: action.reducedTypes,
                showOriginalSignal: action.showOriginalSignal,
                actorId: action.actorId,
                notes: action.notes,
                modelCardUri: ARTIFACT_REDUCTION_MODEL_CARD_URI,
              }).catch(() => {
                // Best-effort audit persist.
              });
            }}
          />
          <ArtifactReviewDashboard
            recordingId={recordingId || "unknown"}
            items={artifactWorkbenchRows}
            onDecide={(artifactId, decision) => {
              setArtifactDecisions((current) => ({
                ...current,
                [artifactId]: decision,
              }));
              const row = artifactWorkbenchRows.find(
                (item) => item.artifactId === artifactId,
              );
              if (!recordingId || !studyId || !row) return;
              const acceptAfterHash =
                row.afterTraceHash !== row.beforeTraceHash
                  ? row.afterTraceHash
                  : hashTraceSamples(
                      Array.from({ length: 32 }, (_, i) => i + row.startSeconds),
                    ).slice(0, 8) + "-red";
              void persistArtifactQcDecision({
                studyId,
                recordingId,
                artifactId,
                decision,
                detectorId: row.detectorId,
                channel: row.channel,
                startSeconds: row.startSeconds,
                endSeconds: row.endSeconds,
                beforeTraceHash: row.beforeTraceHash,
                afterTraceHash:
                  decision === "accept" ? acceptAfterHash : row.beforeTraceHash,
              }).catch(() => {
                // Keep local decision; audit persist is best-effort for S3.
              });
            }}
          />
        </div>
      </RegulatoryPhaseGate>
    );
  }

  if (panelKey === "clinical_timeline") {
    return (
      <ClinicalTimelinePanel
        events={timelineEvents}
        currentTimeSeconds={currentTime}
        densityMode={densityMode}
        onDensityModeChange={setDensityMode}
        enabledKinds={enabledKinds}
        onEnabledKindsChange={setEnabledKinds}
        onJumpToEvent={(event) => onSeek(event.startTimeSeconds)}
      />
    );
  }

  if (panelKey === "review_accel") {
    const eventTypes = Array.from(
      new Set(timelineEvents.map((event) => event.eventTypeCode)),
    ).slice(0, 8);
    return (
      <ReviewAccelerationToolkit
        densityStripVisible={review.state.densityStripVisible}
        onToggleDensityStrip={review.toggleDensityStrip}
        densityBuckets={densityBuckets}
        recordingDurationSeconds={recordingDuration || 1}
        currentTimeSeconds={currentTime}
        bookmarks={review.state.bookmarks}
        clipQueue={review.state.clipQueue}
        selectedEventType={review.state.selectedEventType}
        eventTypeOptions={eventTypes}
        baselineComparisonEnabled={review.state.baselineComparisonEnabled}
        onBookmarkCurrent={() =>
          review.addBookmark(
            toBookmarkEvent(recordingId || "", studyId || "", currentTime),
          )
        }
        onRemoveBookmark={review.removeBookmark}
        onEnqueueCurrentClip={() =>
          review.enqueueClip({
            ...toBookmarkEvent(recordingId || "", studyId || "", currentTime),
            eventId: `clip-${Date.now()}`,
            kind: "clip",
            eventTypeCode: "clip",
            eventLabel: `Clip @ ${currentTime.toFixed(1)}s`,
            payload: { kind: "clip" },
          })
        }
        onDequeueClip={review.dequeueClip}
        onSelectEventType={review.setEventType}
        onToggleBaselineComparison={review.toggleBaselineComparison}
        onJumpToEvent={(event) => onSeek(event.startTimeSeconds)}
        onSeek={onSeek}
      />
    );
  }

  if (panelKey === "clinical_collab") {
    return (
      <ClinicalReviewCollaborationPanel
        reviewers={[
          {
            userId: "self",
            displayName: "You",
            role: "reviewer",
            lastSeenAt: new Date().toISOString(),
            active: true,
          },
        ]}
        consensusStatus={consensusStatus}
        onConsensusStatusChange={setConsensusStatus}
        handoffNote={handoffNote}
        onHandoffNoteChange={setHandoffNote}
        auditSummaries={[
          {
            id: "audit-1",
            summary: "Reviewer opened recording (PHI redacted summary).",
            createdAt: new Date().toISOString(),
          },
        ]}
        reviewQueueHref="/reports/review"
        supportEscalationHref="/help"
      />
    );
  }

  return null;
}
